import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import CartDrawer from '../components/common/CartDrawer';
import api from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

function ProductCard({ product }) {
  return (
    <Link to={`/products/${product._id}`}
      className="group bg-white card-industrial flex flex-col overflow-hidden">
      <div className="h-52 bg-surface-container overflow-hidden relative">
        {product.images?.[0]
          ? <img src={product.images[0]} alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          : <div className="w-full h-full flex items-center justify-center bg-surface-container">
              <span className="material-symbols-outlined text-[64px] text-outline-variant">bolt</span>
            </div>
        }
        {product.grade && (
          <span className="absolute top-3 left-3 badge badge-orange text-[10px]">{product.grade}</span>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <p className="font-label text-[10px] uppercase tracking-[0.2em] text-secondary">
          {product.category?.name}{product.subcategory ? ` · ${product.subcategory.name}` : ''}
        </p>
        <h3 className="font-headline font-bold text-lg uppercase tracking-tight mt-1 text-on-surface leading-tight">{product.name}</h3>
        {product.material && (
          <p className="font-body text-xs text-secondary mt-1">{product.material} {product.finish ? `· ${product.finish}` : ''}</p>
        )}
        <div className="mt-auto pt-4 flex items-center justify-between border-t border-surface-container mt-3">
  {product.sizes?.length > 0 && (
    <span className="font-label text-xs text-secondary">
      {product.sizes.length} size{product.sizes.length !== 1 ? 's' : ''}
    </span>
  )}
  <span className="flex items-center gap-1 font-label text-xs font-bold text-primary-container uppercase tracking-wider group-hover:gap-2 transition-all ml-auto">
    View Details <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
  </span>
</div>
      </div>
    </Link>
  );
}

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products,      setProducts]      = useState([]);
  const [categories,    setCategories]    = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [pagination,    setPagination]    = useState({});
  const [sidebarOpen,   setSidebarOpen]   = useState(false);

  const categoryId    = searchParams.get('category')    || '';
  const subcategoryId = searchParams.get('subcategory') || '';
  const searchQ       = searchParams.get('search')      || '';
  const page          = parseInt(searchParams.get('page') || '1');

  // Load categories
  useEffect(() => {
    api.get('/categories/all').then(r => setCategories(r.data.data || []));
  }, []);

  // Load subcategories when category changes
  useEffect(() => {
    if (categoryId) {
      api.get(`/subcategories?category=${categoryId}`).then(r => setSubcategories(r.data.data || []));
    } else {
      setSubcategories([]);
    }
  }, [categoryId]);

  // Load products
  const loadProducts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (categoryId)    params.set('category',    categoryId);
    if (subcategoryId) params.set('subcategory', subcategoryId);
    if (searchQ)       params.set('search',      searchQ);
    params.set('page',  page);
    params.set('limit', '12');

    api.get(`/products?${params}`)
      .then(r => { setProducts(r.data.data || []); setPagination(r.data.pagination || {}); })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [categoryId, subcategoryId, searchQ, page]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const setFilter = (key, val) => {
    const next = new URLSearchParams(searchParams);
    if (val) next.set(key, val); else next.delete(key);
    if (key !== 'page') next.delete('page');
    if (key === 'category') next.delete('subcategory');
    setSearchParams(next);
  };

  const sortedProducts = [...products].sort((a, b) =>  a.name.localeCompare(b.name, undefined, { numeric: true }));

  const activeCat  = categories.find(c => c._id === categoryId);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <CartDrawer />

      <main className="flex-1 pt-20">
        {/* Page header */}
        <div className="bg-on-surface text-white px-6 md:px-10 py-10">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-2 font-label text-xs text-gray-500 uppercase tracking-widest mb-3">
              <Link to="/" className="hover:text-gray-300">Home</Link>
              <span>/</span>
              <span className="text-gray-400">Products</span>
              {activeCat && <><span>/</span><span className="text-primary-container">{activeCat.name}</span></>}
            </div>
            <h1 className="font-headline text-4xl md:text-5xl uppercase tracking-tight">
              {searchQ ? `Search: "${searchQ}"` : activeCat ? activeCat.name : 'All Products'}
            </h1>
            {!loading && (
              <p className="font-label text-sm text-gray-400 mt-2">{pagination.total || 0} products found</p>
            )}
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 py-10 flex gap-8">

          {/* Sidebar */}
          <>
            {/* Mobile overlay */}
            {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />}

            <aside className={`
              fixed md:sticky top-0 md:top-24 left-0 h-full md:h-auto w-72 md:w-60 lg:w-64
              bg-white z-40 md:z-auto shadow-xl md:shadow-none
              transition-transform duration-300 overflow-y-auto
              ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
              flex-shrink-0 border-r md:border border-outline-variant/30
            `}>
              <div className="p-5">
                {/* Mobile close */}
                <div className="flex items-center justify-between mb-5 md:hidden">
                  <span className="font-headline font-black text-lg uppercase">Filters</span>
                  <button onClick={() => setSidebarOpen(false)}>
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </div>

                {/* All products */}
                <div className="mb-6">
                  <h3 className="font-label text-[10px] uppercase tracking-[0.25em] text-secondary mb-3">Browse By</h3>
                  <button onClick={() => { setFilter('category', ''); setSidebarOpen(false); }}
                    className={`w-full text-left px-3 py-2.5 font-label text-sm font-semibold transition-colors ${!categoryId ? 'bg-primary-container text-white' : 'text-on-surface hover:bg-surface-container'}`}>
                    All Products
                  </button>
                </div>

                {/* Categories */}
                <div>
                  <h3 className="font-label text-[10px] uppercase tracking-[0.25em] text-secondary mb-3">Categories</h3>
                  <div className="space-y-0.5">
                    {categories.map(cat => (
                      <div key={cat._id}>
                        <button onClick={() => { setFilter('category', cat._id); setSidebarOpen(false); }}
                          className={`w-full text-left px-3 py-2.5 font-label text-sm font-semibold transition-colors flex items-center justify-between ${
                            categoryId === cat._id ? 'bg-on-surface text-primary-container' : 'text-on-surface hover:bg-surface-container'
                          }`}>
                          {cat.name}
                          {categoryId === cat._id && <span className="material-symbols-outlined text-[16px]">chevron_right</span>}
                        </button>

                        {/* Subcategories */}
                        {categoryId === cat._id && subcategories.length > 0 && (
                          <div className="ml-3 border-l-2 border-primary-container/30 pl-3 mt-1 space-y-0.5">
                            <button onClick={() => { setFilter('subcategory', ''); setSidebarOpen(false); }}
                              className={`w-full text-left px-2 py-2 font-label text-xs transition-colors ${!subcategoryId ? 'text-primary-container font-bold' : 'text-secondary hover:text-on-surface'}`}>
                              All {cat.name}
                            </button>
                            {subcategories.map(sub => (
                              <button key={sub._id} onClick={() => { setFilter('subcategory', sub._id); setSidebarOpen(false); }}
                                className={`w-full text-left px-2 py-2 font-label text-xs transition-colors ${
                                  subcategoryId === sub._id ? 'text-primary-container font-bold' : 'text-secondary hover:text-on-surface'
                                }`}>
                                {sub.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Search */}
                {searchQ && (
                  <div className="mt-6 pt-6 border-t border-surface-container">
                    <h3 className="font-label text-[10px] uppercase tracking-[0.25em] text-secondary mb-2">Search</h3>
                    <div className="flex items-center gap-2 bg-surface-container px-3 py-2">
                      <span className="font-label text-xs text-on-surface flex-1 truncate">"{searchQ}"</span>
                      <button onClick={() => setFilter('search', '')} className="text-secondary hover:text-error">
                        <span className="material-symbols-outlined text-[16px]">close</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Mobile filter toggle */}
            <div className="flex items-center justify-between mb-6 md:hidden">
              <button onClick={() => setSidebarOpen(true)} className="flex items-center gap-2 btn-secondary py-2 px-4 text-sm">
                <span className="material-symbols-outlined text-[18px]">tune</span>
                Filters
              </button>
              <span className="font-label text-xs text-secondary">{pagination.total || 0} products</span>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => <div key={i} className="h-72 skeleton" />)}
              </div>
            ) : products.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-32 gap-4 text-center">
                <span className="material-symbols-outlined text-[80px] text-outline-variant">search_off</span>
                <h3 className="font-headline font-black text-2xl uppercase text-secondary">No products found</h3>
                <p className="font-body text-sm text-secondary">Try a different category or search term.</p>
                <button onClick={() => setSearchParams({})} className="btn-primary">Clear Filters</button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sortedProducts.map(p => <ProductCard key={p._id} product={p} />)}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-12">
                    <button disabled={page <= 1} onClick={() => setFilter('page', page - 1)}
                      className="px-4 py-2 border border-outline-variant font-label text-sm disabled:opacity-40 hover:bg-surface-container transition-colors">
                      <span className="material-symbols-outlined text-[18px]">chevron_left</span>
                    </button>
                    {[...Array(pagination.pages)].map((_, i) => (
                      <button key={i} onClick={() => setFilter('page', i + 1)}
                        className={`w-10 h-10 font-label text-sm font-bold transition-colors ${
                          page === i + 1 ? 'bg-primary-container text-white' : 'border border-outline-variant hover:bg-surface-container'
                        }`}>
                        {i + 1}
                      </button>
                    ))}
                    <button disabled={page >= pagination.pages} onClick={() => setFilter('page', page + 1)}
                      className="px-4 py-2 border border-outline-variant font-label text-sm disabled:opacity-40 hover:bg-surface-container transition-colors">
                      <span className="material-symbols-outlined text-[18px]">chevron_right</span>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
